import { spacingFluidSmall } from "@porsche-design-system/components-js/styles";
import {
  PFieldsetWrapper,
  PTextFieldWrapper,
  PText,
} from "@porsche-design-system/components-react";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatGpsPickup,
  isEmbeddedWindow,
  resolveGeolocationErrorKey,
} from "./geolocationPickup";

const formatMoney = (amount) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
  }).format(Number(amount || 0));

const CustomPickup = ({ textHeader, formData, setFormData, pickupConfig }) => {
  const [pickupError, setPickupError] = useState(false);
  const [pickupTouched, setPickupTouched] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState("");

  const { isTuk } = useContext(AuthContext);
  const { t } = useTranslation(["tourInfo", "confirmationScreen"]);
  const isEmbedded = isEmbeddedWindow();
  const required = Boolean(pickupConfig?.required);
  const showCheckbox = Boolean(pickupConfig?.showCheckbox);
  const showInfoBox = pickupConfig?.showInfoBox !== false;
  const gpsEnabled = Boolean(pickupConfig?.gpsEnabled);
  const hasAddress = Boolean(String(formData.customPickup || formData.customPickUp || "").trim());
  const requested = required || Boolean(formData.customPickupRequested) || hasAddress;
  const shouldShowField = required || !showCheckbox || requested;
  const fieldIsRequired = required || requested;
  const hasSurcharge = Boolean(pickupConfig?.feeEnabled && pickupConfig?.feeAmount > 0);
  const radiusKm = pickupConfig?.radiusKm;
  const pickupPlaceholder =
    pickupConfig?.placeholder ||
    t("confirmationScreen:form.customPickup.ph", "Tu direccion");
  const promptTitle = required
    ? t("confirmationScreen:form.customPickup.requiredRequest", "Indica tu punto de recogida")
    : showCheckbox
      ? t("confirmationScreen:form.customPickup.request", "Quiero recogida personalizada")
      : t("confirmationScreen:form.customPickup.directRequest", "Recogida personalizada");
  const promptDescription = (
    <>
      {hasSurcharge
        ? t("confirmationScreen:form.customPickup.surcharge", {
            price: formatMoney(pickupConfig.feeAmount),
            defaultValue: `Tiene un coste extra de ${formatMoney(pickupConfig.feeAmount)}.`,
          })
        : t("confirmationScreen:form.customPickup.free", "Sin coste extra.")}
      {radiusKm
        ? ` ${t("confirmationScreen:form.customPickup.radius", {
            km: radiusKm,
            defaultValue: `Disponible dentro de ${radiusKm} km.`,
          })}`
        : ""}
    </>
  );

  const handleChange = (e) => {
    const value = e.target.value;
    const nextRequested = required || (showCheckbox ? requested : Boolean(value.trim()));
    const nextFieldIsRequired = required || nextRequested;
    setFormData({
      ...formData,
      customPickup: value,
      customPickUp: value,
      customPickupRequested: nextRequested,
    });
    if (pickupTouched) {
      setPickupError(nextFieldIsRequired && !value.trim());
    }
  };

  const applyPickupValue = (value) => {
    setPickupTouched(false);
    setPickupError(false);
    setGpsError("");
    setFormData({
      ...formData,
      customPickup: value,
      customPickUp: value,
      customPickupRequested: required || Boolean(String(value || "").trim()),
    });
  };

  const handleBlur = (e) => {
    setPickupTouched(true);
    setPickupError(fieldIsRequired && !e.target.value.trim());
  };

  const handleToggle = (event) => {
    const checked = event.target.checked;
    setPickupTouched(false);
    setPickupError(false);
    setFormData({
      ...formData,
      customPickupRequested: checked,
      customPickup: checked ? formData.customPickup || "" : "",
      customPickUp: checked ? formData.customPickUp || "" : "",
    });
  };

  const gpsErrorText = (key) =>
    t(`confirmationScreen:form.customPickup.${key}`, {
      defaultValue: t(
        "confirmationScreen:form.customPickup.gpsError",
        "No pudimos obtener tu ubicacion actual. Intentalo de nuevo."
      ),
    });

  const setResolvedGpsError = (error, overrides = {}) => {
    const errorKey = resolveGeolocationErrorKey({
      error,
      embedded: isEmbedded,
      secureContext:
        typeof window === "undefined" ? true : window.isSecureContext,
      supported:
        typeof navigator !== "undefined" && Boolean(navigator.geolocation),
      ...overrides,
    });

    setGpsError(gpsErrorText(errorKey));
  };

  const handleUseCurrentLocation = async () => {
    const geolocationSupported =
      typeof navigator !== "undefined" && Boolean(navigator.geolocation);
    const secureContext =
      typeof window === "undefined" ? true : window.isSecureContext;

    if (!geolocationSupported || !secureContext) {
      setResolvedGpsError(null, {
        supported: geolocationSupported,
        secureContext,
      });
      return;
    }

    setGpsLoading(true);
    setGpsError("");

    try {
      if (navigator.permissions?.query) {
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });

        if (permission.state === "denied") {
          setResolvedGpsError({ code: 1 });
          setGpsLoading(false);
          return;
        }
      }
    } catch {
      // Some browsers do not support querying geolocation permission reliably.
      // We still call getCurrentPosition below; that is the source of truth.
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        applyPickupValue(formatGpsPickup(coords));
        setGpsLoading(false);
      },
      (error) => {
        setResolvedGpsError(error);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  return (
    <PFieldsetWrapper label={textHeader}>
      <div className="flex flex-col" style={{ gap: spacingFluidSmall }}>
        {showInfoBox && (
          <label className="flex items-start gap-3 rounded-xl border border-slate-300/40 bg-white/80 p-3 text-sm text-slate-900">
            {showCheckbox && (
              <input
                type="checkbox"
                checked={requested}
                onChange={handleToggle}
                className="mt-1"
              />
            )}
            <span>
              <strong className="block">{promptTitle}</strong>
              <span className="block opacity-80">{promptDescription}</span>
            </span>
          </label>
        )}

        {!showInfoBox && showCheckbox && (
          <label className="flex items-center gap-3 text-sm text-slate-900">
            <input
              type="checkbox"
              checked={requested}
              onChange={handleToggle}
            />
            <span>{promptTitle}</span>
          </label>
        )}

        {shouldShowField && (
          <div className="flex flex-col" style={{ gap: spacingFluidSmall }}>
            <PTextFieldWrapper
              className="w-full"
              state={pickupError && pickupTouched ? "error" : undefined}
              message={
                pickupError && pickupTouched
                  ? t("confirmationScreen:form.customPickup.required", "Indica una direccion de recogida.")
                  : undefined
              }
            >
              <input
                type="text"
                className="w-full"
                value={formData.customPickup || ""}
                onChange={handleChange}
                placeholder={pickupPlaceholder}
                required={fieldIsRequired}
                onBlur={handleBlur}
              />

              {isTuk && (
                // => Este contenedor es el que entra en el slot y ocupa el 100%
                <div slot="description" className="block w-full">
                  <div className="whitespace-pre-wrap flex flex-col bg-slate-200 w-full rounded-md p-3">
                    <PText>{t("accordion.pickup.content.1") || "Loading..."}</PText>
                    <PText>
                      {(t("accordion.pickup.content.2") || "Loading...") + " "}
                      {t("accordion.pickup.content.4") || "Loading..."}
                    </PText>
                  </div>
                </div>
              )}
            </PTextFieldWrapper>

            {gpsEnabled && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={gpsLoading}
                  className="self-start rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {gpsLoading
                    ? t("confirmationScreen:form.customPickup.gpsLoading", "Obteniendo ubicacion...")
                    : t("confirmationScreen:form.customPickup.useGps", "Usar mi ubicacion actual")}
                </button>
                {gpsError ? (
                  <span className="text-sm text-rose-600">{gpsError}</span>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </PFieldsetWrapper>
  );
};

export default CustomPickup;
